/**
 * Shared contract for the redesigned company onboarding, used by both entry
 * points (`/auth/onboarding/company` and `/company/create`).
 *
 * Two naming worlds meet here and must never blur:
 * - **form values** (`CompanyOnboardingValues`) use the camelCase model the
 *   prototype defined, because every promoted component (review rows, focal
 *   flow, business card) is keyed off it;
 * - **wire payloads** (`CompanyCreationWirePayload`, `CompanyImportData`) use the
 *   BE contract names (`brain/raw/reference/company-api-contract.md`).
 *
 * `buildCreationPayload` (`utils/payload.ts`) is the *only* boundary allowed to
 * translate between them. Nothing else in the flow maps form keys to wire keys.
 */

import type { EUVatCountryCode } from "@/constants/vat";
import type { RemotePolicy } from "@/domains/job-offer/create/constants";

/** Which entry point mounted the flow: decides endpoint, tracking and CTA target. */
export type CompanyOnboardingSource = "onboarding" | "creation";

/** Screens of the flow. All are internal states — none has its own URL. */
export type CompanyOnboardingPhase =
  | "url"
  | "loading"
  | "review"
  | "manual"
  | "build"
  | "success";

/**
 * Size buckets of the contract; the LinkedIn prefill already returns this scale
 * (the BE maps LinkedIn's own employee ranges onto it before answering).
 *
 * The list is the declaration and the union is derived from it, not the other
 * way round. Written as a union, the scale had to be repeated as a "runtime
 * mirror" beside every consumer that needed to *enumerate* it — the Zod enum and
 * the import guard each carried their own copy, and nothing made the three agree.
 * One declaration means a bucket added here is a bucket the schema, both selects
 * and the guard all learn about at once.
 */
export const COMPANY_SIZE_BUCKETS = [
  "1-10",
  "10-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+",
] as const;

export type CompanySizeBucket = (typeof COMPANY_SIZE_BUCKETS)[number];

/** Structured location (jsonb column). `headquarter` can be false on *every* entry. */
export interface CompanyLocation {
  /** Already formatted for display: "Los Gatos, CA, United States". */
  text: string;
  city?: string;
  state?: string;
  /** Extended name: "United States". */
  country?: string;
  /** ISO 2 letters: "US". */
  countryCode?: string;
  headquarter: boolean;
}

/**
 * Form values shared by the review, the manual path and the payload builder.
 *
 * Logo and cover are deliberately absent: they are never editable during
 * creation, arrive only from the import, and live in `CompanyImportOutcome`.
 */
export interface CompanyOnboardingValues {
  companyName: string;
  website: string;
  invoiceEmail: string;
  invoiceAddress: string;
  invoiceCountry: EUVatCountryCode;
  /** Optional (contract §2.4). Empty string means "not provided". */
  invoiceNumber: string;
  description: string;
  industry: string;
  size: CompanySizeBucket | "";
  headquarters: string;
  foundedYear: string;
  /** Optional, and collected only by the manual path (SPEC OQ 2 / PLAN D8). */
  remotePolicy: RemotePolicy | "";
}

export type CompanyOnboardingFieldKey = keyof CompanyOnboardingValues;

/**
 * A logo or a cover chosen on the manual path, held until there is a company to
 * attach it to (`POST /company/{logo,cover}/:companyId`, contract §4 — both
 * endpoints are keyed on an id that does not exist during creation, and §1.3
 * refuses any URL outside our own bucket, so neither can travel in the creation
 * body either).
 *
 * Deliberately outside `CompanyOnboardingValues`: those values are serialized
 * into the draft, and a `File` does not survive that round trip. The pick lives
 * as long as the tab does.
 */
export interface CompanyImagePick {
  readonly file: File;
  /** Data URL — the review preview and the finale card both paint this. */
  readonly preview: string;
}

/**
 * Rows that were deduced from the payload but are not certain, and therefore
 * carry the yellow "da confermare" badge until the user opens and closes them.
 *
 * `invoiceAddress` is deliberately *not* here. The import can never propose a
 * billing address — `CompanyLocation` carries no street and no postal code — so
 * a "da confermare" state for it would be a branch that can never be reached.
 */
export type ConfirmableField = Extract<
  CompanyOnboardingFieldKey,
  "industry" | "headquarters" | "invoiceCountry"
>;

/** Payload of `POST /company/linkedin/fetch`. Absent fields are omitted, never null. */
export interface CompanyImportData {
  /** The only field always present on a successful fetch. */
  name: string;
  description?: string;
  website?: string;
  /** Already mirrored onto our bucket — never a `media.licdn.com` URL. */
  logo_url?: string;
  cover_image_url?: string;
  industry?: string;
  company_size?: CompanySizeBucket;
  founded_year?: string;
  headquarters_location?: string;
  locations?: CompanyLocation[];
  linkedin_url?: string;
}

/**
 * Result of a successful import, once mapped onto the form.
 *
 * Everything here must survive `JSON.stringify` — it is persisted in the draft
 * (hence arrays rather than `Set`s for the field collections).
 */
export interface CompanyImportOutcome {
  /** Verbatim response of the fetch. Never mutated: the BE revalidates it (§1.3). */
  readonly data: CompanyImportData;
  /** Form keys filled from the payload; drive the LinkedIn glyph on the rows. */
  readonly importedFields: readonly CompanyOnboardingFieldKey[];
  /** Deduced-but-uncertain rows; drive the yellow "da confermare" badge. */
  readonly fieldsToConfirm: readonly ConfirmableField[];
  /**
   * How many fields actually came from the payload. Derived values (proposed
   * billing address, VAT country, account email) are never counted — below
   * `POOR_IMPORT_THRESHOLD` the header stops celebrating (PLAN D10).
   */
  readonly importedCount: number;
}

/**
 * Body sent to `POST /company/onboarding` and `POST /company/v2` (inner
 * `company` object). Optional keys are **omitted** when empty, never `null` and
 * never `""` — the BE validates the four prefill fields with Zod and a single
 * manipulated value fails the whole creation (§1.3).
 */
export interface CompanyCreationWirePayload {
  name: string;
  invoice_email: string;
  invoice_address: string;
  description?: string;
  website?: string;
  industry?: string;
  company_size?: string;
  /** String, not number, per the contract. */
  founded_year?: string;
  headquarters_location?: string;
  social_media?: { linkedin?: string };
  remote_work_policy?: string;
  /** Composed as `country + number`, and only when the number is non-empty. */
  invoice_vat?: string;
  logo_url?: string;
  cover_image_url?: string;
  /** Forwarded exactly as received; picking a headquarter never mutates them. */
  locations?: CompanyLocation[];
  linkedin_url?: string;
}

/**
 * Single-question screens of the manual path, in order (SPEC AC 14).
 *
 * Declared here rather than beside the schema because the draft persists it and
 * `validators/schema.ts` already imports from this module — the other direction
 * would be a cycle. The runtime array lives in the schema and is checked against
 * this union with `satisfies`, so the two cannot drift.
 */
export type CompanyOnboardingManualStep =
  | "nome"
  | "fatturazione"
  | "piva"
  | "racconta"
  | "dettagli"
  | "conferma";

/** Bump when the persisted shape changes: unknown versions are dropped silently. */
export const COMPANY_ONBOARDING_DRAFT_VERSION = 1;

/**
 * Locally persisted draft, so a reload during a ~120s scrape or a long review
 * does not cost the user their work (SPEC AC 23). Keyed per user.
 */
export interface CompanyOnboardingDraft {
  version: typeof COMPANY_ONBOARDING_DRAFT_VERSION;
  userId: string;
  source: CompanyOnboardingSource;
  phase: CompanyOnboardingPhase;
  values: CompanyOnboardingValues;
  importOutcome: CompanyImportOutcome | null;
  /**
   * Which manual screen the user had reached. `phase` alone is not enough: a
   * reload halfway through the manual path must come back to the same question,
   * not to its first one (Gate M scenario 3). `null` whenever `phase` is not
   * `"manual"`.
   */
  manualStep: CompanyOnboardingManualStep | null;
  /** URL typed on the entry screen, kept so an interrupted import can resume. */
  linkedInUrl: string;
  /**
   * ISO timestamp of the 429 that closed the import, or `null` if none has been
   * seen.
   *
   * The latch that refuses a second paid scrape lives in the import controller,
   * which dies with the page — so a reload came back with the link still typed
   * and a live "Importa", on a budget that is per-IP, windowed, and *extended*
   * by every rejected request. This is what carries the refusal across.
   *
   * A stamp rather than a flag because the window does expire: a boolean would
   * lock the import out of this device forever. See `isRateLimitStillOn`.
   */
  rateLimitedAt: string | null;
  /** ISO timestamp of the last write. */
  savedAt: string;
}
